// ============= Route Validation and Title Management =============

/**
 * Validates if a given path is a legitimate route in the application
 * @param path - The URL path to validate
 * @returns boolean indicating if the path is valid
 */
export const isValidRoute = (path: string): boolean => {
  const validStaticRoutes = ['/', '/files', '/people'];
  const validDynamicPrefixes = ['/request/', '/activities/', '/files/'];
  const validWorkflowPrefix = '/workflows/';

  // Check if it's a static route
  if (validStaticRoutes.includes(path)) {
    return true;
  }

  // Check if it's a workflow route
  if (path.startsWith(validWorkflowPrefix)) {
    // All paths starting with /workflows/ are considered valid
    return true;
  }

  // Check if it's a dynamic route with ID
  return validDynamicPrefixes.some((prefix) => {
    if (path.startsWith(prefix)) {
      const remainingPath = path.slice(prefix.length);
      // Ensure there's an ID after the prefix and no additional slashes
      return remainingPath.length > 0 && !remainingPath.includes('/');
    }
    return false;
  });
};

const WORKFLOW_PATH_PREFIXES = [
  '/workflows/',
  '/request/',
  '/activities/',
  '/files/',
] as const;

/**
 * Extracts the workflow registry key from a pathname when present.
 * Returns undefined on dashboard and other non-workflow routes.
 */
export const getWorkflowKeyFromPath = (path: string): string | undefined => {
  const prefix = WORKFLOW_PATH_PREFIXES.find((item) => path.startsWith(item));
  if (!prefix) {
    return undefined;
  }

  const segment = path.slice(prefix.length).split('/')[0];
  return segment || undefined;
};

/**
 * Gets the page title based on the current path
 * @param path - The URL path
 * @returns The appropriate page title
 */
export const getPageTitle = (path: string): string => {
  switch (path) {
    case '/':
      return 'Dashboard';
    case '/files':
      return 'Files';
    case '/people':
      return 'People';
    default:
      if (path.startsWith('/request/')) {
        return 'Request Details';
      }
      if (path.startsWith('/activities/')) {
        return 'Activity Details';
      }
      if (path.startsWith('/files/')) {
        return 'File Details';
      }
      if (path.startsWith('/workflows/')) {
        // Extract the workflow name from the path
        const workflowName = path.replace('/workflows/', '');
        // Convert kebab-case to Title Case
        return workflowName
          .split('-')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }
      return 'Not Found';
  }
};
